import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { fetchCateringMenuItemsForTemplate } from './cateringMenuApi';
import { formatFoodItemDisplayName } from './onlineFoodItems';
import defaultFoodImage from './assets/DefaultFood.jpg';
import MealTypesDropdown, { canonicalMealsCsv, parseMealsCsv } from './mealTypesDropdown';
import {
    addCateringTempPackageItems,
    deleteCateringTempPackageItem,
    getPackageItemRowKey,
    normalizeCateringTempPackageItem,
    packageCateringItemIdsInUse,
    packageItemToCreatePayload,
    packageItemToUpdatePayload,
    updateCateringTempPackageItem,
} from './cateringTempApi';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, sortList, str } from './tableSortUtils';
import './css/OrdersTablePagination.css';
import { confirmAction } from './ui/feedback';

function defaultPickerRowDraft() {
    return { meal: '' };
}

function getPickerRowDraft(drafts, foodId) {
    return drafts[foodId] ?? defaultPickerRowDraft();
}

function getPackageItemSortValue(item, sortKey) {
    const row = normalizeCateringTempPackageItem(item) || item || {};
    switch (sortKey) {
        case 'itemName':
            return str(row.itemName);
        case 'category':
            return str(row.foodCategory);
        case 'meal':
            return str(row.meal);
        case 'price':
            return Number.parseFloat(row.price) || 0;
        default:
            return '';
    }
}

function draftItemFromMenuRow(menuRow, rowDraft) {
    const cateringItemId = menuRow.Catering_Item_Id ?? menuRow.cateringItemId;
    return normalizeCateringTempPackageItem({
        localKey: `local-${cateringItemId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        cateringItemId,
        itemName: menuRow.Food_Item ?? menuRow.Item_Name ?? '',
        price: menuRow.Price ?? menuRow.price ?? '',
        foodItemImageUrl: menuRow.imageUrl ?? menuRow.foodItemImageUrl ?? null,
        foodCategory: menuRow.Food_Category ?? menuRow.foodCategory ?? '',
        foodType: menuRow.itemFoodType ?? menuRow.Food_Type ?? '',
        meal: canonicalMealsCsv(rowDraft.meal ?? ''),
    });
}

function itemFormFromRow(item) {
    const row = normalizeCateringTempPackageItem(item) || item;
    return {
        meal: canonicalMealsCsv(row.meal ?? ''),
    };
}

const CateringTemplateItemsEditor = ({
    restaurantId,
    items = [],
    onItemsChange,
    packageId = null,
    user = null,
    loading = false,
    disabled = false,
    deferItemPersist = false,
    showTitle = true,
    title = 'Package items',
    headerClassName = 'menu-template-items-editor-header',
    emptyMessage = 'No items added yet. Click Add from menu to select catering items.',
}) => {
    const [menuFoodItems, setMenuFoodItems] = useState([]);
    const [showMenuPicker, setShowMenuPicker] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemForm, setItemForm] = useState({ meal: '' });
    const [savingItem, setSavingItem] = useState(false);
    const [menuPickerSearch, setMenuPickerSearch] = useState('');
    const [selectedFoodIds, setSelectedFoodIds] = useState(() => new Set());
    const [pickerRowDrafts, setPickerRowDrafts] = useState({});
    const [menuLoadError, setMenuLoadError] = useState('');
    const [itemsPage, setItemsPage] = useState(1);
    const [itemsPageSize, setItemsPageSize] = useState(10);
    const [itemsSort, setItemsSort] = useState(null);

    const isPersisted = packageId != null && String(packageId).trim() !== '';
    const shouldPersistImmediately = isPersisted && !deferItemPersist;

    const existingCateringItemIds = useMemo(
        () => packageCateringItemIdsInUse(items),
        [items]
    );

    const loadMenuFoodItems = useCallback(async () => {
        if (!restaurantId) return;
        setMenuLoadError('');
        try {
            const cateringItems = await fetchCateringMenuItemsForTemplate(restaurantId);
            setMenuFoodItems(cateringItems);
        } catch (err) {
            console.error('Error loading catering menu:', err?.response?.data || err?.message);
            setMenuLoadError(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err?.message ||
                    'Failed to load catering menu items.'
            );
            setMenuFoodItems([]);
        }
    }, [restaurantId]);

    useEffect(() => {
        loadMenuFoodItems();
    }, [loadMenuFoodItems]);

    const sortedItems = useMemo(
        () => sortList(items, itemsSort, getPackageItemSortValue),
        [items, itemsSort]
    );

    const { handleSortAsc: handleItemsSortAsc, handleSortDesc: handleItemsSortDesc } =
        buildSortHandlers(setItemsSort, () => setItemsPage(1));

    const totalItemPages = Math.max(1, Math.ceil(sortedItems.length / itemsPageSize));
    const paginatedItems = useMemo(() => {
        const start = (itemsPage - 1) * itemsPageSize;
        return sortedItems.slice(start, start + itemsPageSize);
    }, [sortedItems, itemsPage, itemsPageSize]);

    useEffect(() => {
        if (itemsPage > totalItemPages) setItemsPage(totalItemPages);
    }, [itemsPage, totalItemPages, itemsPageSize]);

    const handleItemsPageSizeChange = (nextSize) => {
        setItemsPageSize(nextSize);
        setItemsPage(1);
    };

    const pickerMenuItems = useMemo(() => {
        const q = menuPickerSearch.trim().toLowerCase();
        return menuFoodItems.filter((m) => {
            const id = String(m.Food_Item_Id ?? m.Catering_Item_Id ?? '');
            if (id && existingCateringItemIds.has(id)) return false;
            if (!q) return true;
            return [m.Food_Item, m.Food_Category, m.Price, m.itemFoodType].some((v) =>
                String(v ?? '').toLowerCase().includes(q)
            );
        });
    }, [menuFoodItems, menuPickerSearch, existingCateringItemIds]);

    const selectedCount = selectedFoodIds.size;
    const allPickerSelected =
        pickerMenuItems.length > 0 &&
        pickerMenuItems.every((m) => selectedFoodIds.has(String(m.Food_Item_Id)));

    const toggleFoodSelection = (foodItemId) => {
        const key = String(foodItemId);
        setSelectedFoodIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleSelectAllPicker = () => {
        if (allPickerSelected) {
            setSelectedFoodIds(new Set());
            return;
        }
        setSelectedFoodIds(new Set(pickerMenuItems.map((m) => String(m.Food_Item_Id))));
    };

    const updatePickerRowDraft = (foodId, patch) => {
        setPickerRowDrafts((prev) => ({
            ...prev,
            [foodId]: { ...getPickerRowDraft(prev, foodId), ...patch },
        }));
    };

    const openMenuPicker = () => {
        if (disabled) return;
        const drafts = {};
        menuFoodItems.forEach((m) => {
            const id = String(m.Food_Item_Id ?? '');
            if (id) drafts[id] = defaultPickerRowDraft();
        });
        setPickerRowDrafts(drafts);
        setSelectedFoodIds(new Set());
        setMenuPickerSearch('');
        setShowMenuPicker(true);
    };

    const closeMenuPicker = () => {
        if (savingItem) return;
        setShowMenuPicker(false);
        setSelectedFoodIds(new Set());
        setPickerRowDrafts({});
        setMenuPickerSearch('');
    };

    const openEditItemModal = (item) => {
        if (disabled) return;
        const normalized = normalizeCateringTempPackageItem(item) || item;
        setEditingItem(normalized);
        setItemForm(itemFormFromRow(normalized));
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        if (savingItem) return;
        setShowEditModal(false);
        setEditingItem(null);
        setItemForm({ meal: '' });
    };

    const handleBulkAdd = async () => {
        if (selectedCount === 0) {
            alert('Select at least one catering item.');
            return;
        }
        const actorId = Number(user?.userId) || Number(restaurantId) || 1;
        const payloads = [];
        const draftRows = [];
        const batchIds = new Set();
        for (const foodId of selectedFoodIds) {
            const row = menuFoodItems.find((m) => String(m.Food_Item_Id) === foodId);
            if (!row) continue;
            const cateringItemId = String(row.Catering_Item_Id ?? row.Food_Item_Id ?? '');
            if (!cateringItemId) continue;
            if (existingCateringItemIds.has(cateringItemId) || batchIds.has(cateringItemId)) {
                alert(`"${row.Food_Item}" is already on this package.`);
                return;
            }
            const draft = getPickerRowDraft(pickerRowDrafts, foodId);
            if (parseMealsCsv(draft.meal).length === 0) {
                alert(`Select at least one meal type for "${row.Food_Item}".`);
                return;
            }
            const itemDraft = draftItemFromMenuRow(row, draft);
            batchIds.add(cateringItemId);
            payloads.push(packageItemToCreatePayload(itemDraft, actorId));
            draftRows.push(itemDraft);
        }
        if (payloads.length === 0) {
            alert('No valid items selected.');
            return;
        }

        setSavingItem(true);
        try {
            if (shouldPersistImmediately) {
                const response = await addCateringTempPackageItems(packageId, payloads, {
                    createdBy: actorId,
                });
                alert(response.data?.message || 'Items added.');
                const nextItems = Array.isArray(response.data?.items)
                    ? response.data.items.map(normalizeCateringTempPackageItem).filter(Boolean)
                    : null;
                onItemsChange(nextItems ?? [...items, ...draftRows]);
            } else {
                onItemsChange([...items, ...draftRows]);
            }
            closeMenuPicker();
        } catch (err) {
            alert(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    err.message ||
                    'Failed to add package items.'
            );
        } finally {
            setSavingItem(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editingItem) return;
        if (parseMealsCsv(itemForm.meal).length === 0) {
            alert('Select at least one meal type.');
            return;
        }
        const rowKey = getPackageItemRowKey(editingItem);
        const actorId = Number(user?.userId) || Number(restaurantId) || 1;
        const updatedLocal = normalizeCateringTempPackageItem({
            ...editingItem,
            meal: canonicalMealsCsv(itemForm.meal),
        });

        setSavingItem(true);
        try {
            if (shouldPersistImmediately && editingItem.cateringTempItemId) {
                const response = await updateCateringTempPackageItem(
                    editingItem.cateringTempItemId,
                    packageItemToUpdatePayload(updatedLocal, actorId)
                );
                alert(response.data?.message || 'Package item updated.');
                const updated = normalizeCateringTempPackageItem(response.data?.item);
                if (updated) {
                    onItemsChange(
                        items.map((row) =>
                            String(getPackageItemRowKey(row)) === String(rowKey) ? updated : row
                        )
                    );
                }
            } else {
                onItemsChange(
                    items.map((row) =>
                        String(getPackageItemRowKey(row)) === String(rowKey) ? updatedLocal : row
                    )
                );
            }
            closeEditModal();
        } catch (err) {
            alert(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    err.message ||
                    'Failed to update package item.'
            );
        } finally {
            setSavingItem(false);
        }
    };

    const handleDeleteItem = async (item) => {
        if (disabled) return;
        const normalized = normalizeCateringTempPackageItem(item) || item;
        const rowKey = getPackageItemRowKey(normalized);
        const label = normalized.itemName || 'this item';
        const ok = await confirmAction({
            title: 'Remove item?',
            message: `Remove "${label}" from this package?`,
            confirmLabel: 'Remove',
            danger: true,
        });
        if (!ok) return;

        setSavingItem(true);
        try {
            if (shouldPersistImmediately && normalized.cateringTempItemId) {
                await deleteCateringTempPackageItem(normalized.cateringTempItemId);
            }
            onItemsChange(items.filter((row) => String(getPackageItemRowKey(row)) !== String(rowKey)));
        } catch (err) {
            alert(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    err.message ||
                    'Failed to remove package item.'
            );
        } finally {
            setSavingItem(false);
        }
    };

    return (
        <>
            <div className="menu-template-items-editor">
                <div className={headerClassName}>
                    {showTitle ? (
                        <h3 className="restaurant-detail-section-title menu-template-items-editor-title">
                            {title}
                        </h3>
                    ) : (
                        <span className="menu-template-items-editor-label">{title}</span>
                    )}
                    <div className="restaurant-detail-actions menu-template-items-editor-actions">
                        <button
                            type="button"
                            className="add-button"
                            onClick={openMenuPicker}
                            disabled={disabled || loading || savingItem}
                            aria-label="Add from menu"
                        >
                            <span className="menu-template-add-btn-label-full">Add from menu</span>
                            <span className="menu-template-add-btn-label-short" aria-hidden="true">
                                Add
                            </span>
                        </button>
                    </div>
                </div>
                {menuLoadError ? (
                    <p className="menu-template-items-load-error">{menuLoadError}</p>
                ) : null}
                <div className="menu-table-wrapper menu-template-items-table-wrapper">
                    <table className="menu-table">
                        <thead>
                            <tr>
                                <OrdersSortableTh
                                    label="Food Item"
                                    sortKey="itemName"
                                    activeSortKey={itemsSort?.key}
                                    sortDirection={itemsSort?.direction}
                                    onSortAsc={handleItemsSortAsc}
                                    onSortDesc={handleItemsSortDesc}
                                />
                                <OrdersSortableTh
                                    label="Food Category"
                                    sortKey="category"
                                    activeSortKey={itemsSort?.key}
                                    sortDirection={itemsSort?.direction}
                                    onSortAsc={handleItemsSortAsc}
                                    onSortDesc={handleItemsSortDesc}
                                />
                                <OrdersSortableTh
                                    label="Meal"
                                    sortKey="meal"
                                    activeSortKey={itemsSort?.key}
                                    sortDirection={itemsSort?.direction}
                                    onSortAsc={handleItemsSortAsc}
                                    onSortDesc={handleItemsSortDesc}
                                />
                                <OrdersSortableTh
                                    label="Price ₹"
                                    sortKey="price"
                                    activeSortKey={itemsSort?.key}
                                    sortDirection={itemsSort?.direction}
                                    onSortAsc={handleItemsSortAsc}
                                    onSortDesc={handleItemsSortDesc}
                                />
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="menu-table-empty-cell menu-empty-filtered">
                                        Loading items…
                                    </td>
                                </tr>
                            ) : items.length > 0 ? (
                                paginatedItems.map((item) => {
                                    const row = normalizeCateringTempPackageItem(item) || item;
                                    const rowKey = getPackageItemRowKey(row);
                                    const imageUrl =
                                        row.foodItemImageUrl ?? row.imageUrl ?? defaultFoodImage;
                                    return (
                                        <tr key={rowKey}>
                                            <td className="menu-template-picker-food-cell">
                                                <div className="menu-food-cell-inner">
                                                <img
                                                    src={imageUrl}
                                                    alt=""
                                                    className="menu-template-item-thumb"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = defaultFoodImage;
                                                    }}
                                                />
                                                <span>
                                                    {formatFoodItemDisplayName(row.itemName) || '—'}
                                                </span>
                                                </div>
                                            </td>
                                            <td className="menu-table-cell-muted">
                                                {row.foodCategory || '—'}
                                            </td>
                                            <td>{row.meal || '—'}</td>
                                            <td>
                                                {row.price != null && row.price !== ''
                                                    ? row.price
                                                    : '—'}
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="action-button"
                                                    title="Edit item"
                                                    onClick={() => openEditItemModal(row)}
                                                    disabled={disabled || savingItem}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="action-button delete-button"
                                                    title="Remove item"
                                                    onClick={() => handleDeleteItem(row)}
                                                    disabled={disabled || savingItem}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5} className="menu-table-empty-cell menu-empty-filtered">
                                        {emptyMessage}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {!loading && items.length > 0 ? (
                    <OrdersTablePagination
                        currentPage={itemsPage}
                        totalPages={totalItemPages}
                        totalItems={sortedItems.length}
                        pageSize={itemsPageSize}
                        onPageChange={setItemsPage}
                        onPageSizeChange={handleItemsPageSizeChange}
                        itemLabel="items"
                        ariaLabel="Package items pagination"
                    />
                ) : null}
            </div>

            {showMenuPicker ? (
                <div
                    className="inventory-modal-overlay"
                    onClick={closeMenuPicker}
                    role="presentation"
                >
                    <div
                        className="inventory-modal menu-template-modal menu-template-picker-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="catering-package-picker-title"
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4 id="catering-package-picker-title">Add from menu</h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                onClick={closeMenuPicker}
                                aria-label="Close"
                                disabled={savingItem}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <div className="menu-template-picker-panel">
                            <div className="menu-template-picker-toolbar">
                                <input
                                    type="text"
                                    className="add-menu-form-field menu-template-picker-search"
                                    placeholder="Search catering menu…"
                                    value={menuPickerSearch}
                                    onChange={(e) => setMenuPickerSearch(e.target.value)}
                                />
                                <div className="menu-template-picker-toolbar-actions">
                                    <button
                                        type="button"
                                        className="close-button menu-template-picker-toolbar-btn"
                                        onClick={toggleSelectAllPicker}
                                        disabled={pickerMenuItems.length === 0}
                                    >
                                        {allPickerSelected ? 'Deselect all' : 'Select all'}
                                    </button>
                                    <button
                                        type="button"
                                        className="add-button menu-template-picker-toolbar-btn"
                                        onClick={handleBulkAdd}
                                        disabled={savingItem || selectedCount === 0}
                                    >
                                        {savingItem
                                            ? 'Adding…'
                                            : `Add selected (${selectedCount})`}
                                    </button>
                                </div>
                            </div>
                            {pickerMenuItems.length > 0 ? (
                                <div className="menu-table-wrapper menu-template-picker-table-wrap">
                                    <table className="menu-table">
                                        <thead>
                                            <tr>
                                                <th
                                                    className="menu-template-picker-check-col"
                                                    aria-hidden="true"
                                                />
                                                <th>Food Item</th>
                                                <th>Food Category</th>
                                                <th>Food Type</th>
                                                <th>Price ₹</th>
                                                <th>Meal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pickerMenuItems.map((m) => {
                                                const fid = String(m.Food_Item_Id);
                                                const checked = selectedFoodIds.has(fid);
                                                const draft = getPickerRowDraft(pickerRowDrafts, fid);
                                                return (
                                                    <tr
                                                        key={fid}
                                                        className={
                                                            checked
                                                                ? 'menu-template-picker-row--selected'
                                                                : ''
                                                        }
                                                    >
                                                        <td className="menu-template-picker-check-col">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => toggleFoodSelection(fid)}
                                                                aria-label={`Select ${m.Food_Item}`}
                                                            />
                                                        </td>
                                                        <td className="menu-template-picker-food-cell">
                                                            <div className="menu-food-cell-inner">
                                                            <img
                                                                src={m.imageUrl || defaultFoodImage}
                                                                alt=""
                                                                className="menu-template-item-thumb"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = defaultFoodImage;
                                                                }}
                                                            />
                                                            <span>
                                                                {formatFoodItemDisplayName(m.Food_Item)}
                                                            </span>
                                                            </div>
                                                        </td>
                                                        <td className="menu-table-cell-muted">
                                                            {m.Food_Category ?? m.foodCategory ?? '—'}
                                                        </td>
                                                        <td>{m.itemFoodType || m.Food_Type || '—'}</td>
                                                        <td>{m.Price ?? m.price ?? '—'}</td>
                                                        <td className="menu-template-picker-field-cell menu-template-picker-meal-cell">
                                                            {checked ? (
                                                                <div className="daily-menu menu-template-meal-dropdown">
                                                                    <MealTypesDropdown
                                                                        value={draft.meal}
                                                                        onCsvChange={(meal) =>
                                                                            updatePickerRowDraft(fid, {
                                                                                meal,
                                                                            })
                                                                        }
                                                                        id={`pkg-picker-${fid}`}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="menu-empty-filtered">
                                    {menuLoadError
                                        ? menuLoadError
                                        : menuFoodItems.length === 0
                                          ? 'No active catering menu items available for this restaurant.'
                                          : 'All active catering items are already in this package, or none match your search.'}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            {showEditModal && editingItem ? (
                <div
                    className="inventory-modal-overlay"
                    onClick={closeEditModal}
                    role="presentation"
                >
                    <div
                        className="inventory-modal menu-template-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4>Edit package item</h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                onClick={closeEditModal}
                                aria-label="Close"
                                disabled={savingItem}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <form
                            className="inventory-form inventory-form--supplier-modal menu-template-edit-form"
                            onSubmit={handleEditSubmit}
                        >
                            <div className="inventory-form-group menu-template-edit-full-row">
                                <label>Food item</label>
                                <input
                                    type="text"
                                    className="add-menu-form-field inventory-readonly-field"
                                    readOnly
                                    disabled
                                    value={editingItem.itemName ?? ''}
                                />
                            </div>
                            <div className="inventory-form-group menu-template-edit-meal-group daily-menu">
                                <label className="menu-form-label--required">Meal types</label>
                                <MealTypesDropdown
                                    value={itemForm.meal}
                                    onCsvChange={(meal) => setItemForm((p) => ({ ...p, meal }))}
                                    id={`pkg-edit-${editingItem.cateringTempItemId ?? editingItem.localKey}`}
                                />
                            </div>
                            <div className="inventory-form-actions menu-template-modal-actions">
                                <button type="submit" className="add-button" disabled={savingItem}>
                                    {savingItem ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default CateringTemplateItemsEditor;
